/**
 * Le transport vers Stripe : ouvrir une session de paiement, et y aller.
 *
 * Une seule fonction. La règle qui décide *si* on propose Bancontact vit dans
 * `stripe.ts`, à côté — ce fichier importe le client Supabase, qui lève au
 * chargement quand les variables d'environnement manquent, donc rien de ce
 * qu'il contient ne s'éprouve.
 *
 * ## Ce qui part d'ici, et ce qui n'en part pas
 *
 * Il part un `invoice_id` et deux adresses de retour. **Il ne part aucun
 * montant.** La fonction edge relit elle-même
 * `v_invoice_balances.outstanding_cents` avant de créer la session.
 *
 * Ce n'est pas de la prudence de principe : l'ancien `create-checkout` de ce
 * dépôt écrivait `unit_amount: Math.round(item.price)` — le prix venu du
 * navigateur — et c'est exactement le défaut que toute la réécriture a servi à
 * fermer.
 *
 * ## Le navigateur n'écrit jamais le paiement
 *
 * Aller chez Stripe, en revenir, ou ne jamais en revenir : rien de tout cela
 * n'enregistre quoi que ce soit. C'est le webhook qui écrit, et lui seul. Un
 * parent qui ferme son onglet après avoir payé doit voir sa facture réglée.
 */

import { supabase } from '../supabase';
import { adressesDeRetour } from './stripe';

/** Une erreur portant une phrase destinée au parent. */
export class ErreurDePaiement extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ErreurDePaiement';
  }
}

const MESSAGE_GENERIQUE =
  "Le paiement en ligne n'a pas pu être ouvert. Votre inscription est enregistrée : "
  + 'vous pouvez payer par virement avec la facture.';

/**
 * Ouvre une session de paiement et **quitte le site** pour la page Stripe.
 *
 * Ne rend rien en cas de succès : la page est remplacée. Lève une
 * `ErreurDePaiement` si la session n'a pas pu être créée — auquel cas le
 * parent reste où il est, inscrit, avec sa facture.
 *
 * `urlConfirmation` est le chemin de retour, paramètres compris. Les deux
 * issues y mènent : c'est la page qui dit au parent que son enfant est
 * inscrit, et elle sait afficher l'une ou l'autre.
 */
export async function payerParStripe(
  invoiceId: string,
  urlConfirmation: string,
): Promise<never | void> {
  const retours = adressesDeRetour(window.location.origin, urlConfirmation);

  let url: string | null = null;
  try {
    const { data, error } = await supabase.functions.invoke('paiement-stripe', {
      body: { invoice_id: invoiceId, ...retours },
    });
    if (error) throw new ErreurDePaiement(MESSAGE_GENERIQUE);
    url = (data as { url?: string } | null)?.url ?? null;
  } catch (e) {
    if (e instanceof ErreurDePaiement) throw e;
    throw new ErreurDePaiement(MESSAGE_GENERIQUE);
  }

  if (!url) throw new ErreurDePaiement(MESSAGE_GENERIQUE);

  /*
    `location.href` plutôt que `location.replace` : le parent doit pouvoir
    revenir en arrière depuis la page Stripe et retrouver sa confirmation.
  */
  window.location.href = url;
}
